/* eslint-disable react/prop-types */
// =============================================================================
// Model Performance Dashboard
// -----------------------------------------------------------------------------
// Analytics-only page for evaluating how well the Portfolio / Trip AI
// recommendations have performed after the fact.
//
// Backed by GET /api/admin/model-performance — aggregates from
// portfolio_decision_outcomes + portfolio_decision_logs + bookings.
// No automation is triggered by anything on this page.
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from 'recharts';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  MinusCircle,
  Pause,
  RefreshCw,
  ShieldAlert,
  TrendingDown,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// ─── Shared design tokens ─────────────────────────────────────────────────────

const OUTCOME_META = {
  won:     { label: 'Won',     pill: 'text-emerald-100 bg-emerald-950/55 border-emerald-400/35', icon: CheckCircle2,  bar: '#34d399' },
  lost:    { label: 'Lost',    pill: 'text-rose-100 bg-rose-950/55 border-rose-400/35',          icon: TrendingDown,  bar: '#f87171' },
  neutral: { label: 'Neutral', pill: 'text-brand-100 bg-brand-950/55 border-brand-400/30',             icon: MinusCircle,   bar: '#4E6199' },
  pending: { label: 'Pending', pill: 'text-stone-300 bg-zinc-900/55 border-white/10',             icon: Pause,         bar: '#71717a' },
};

const FINAL_ACTION_LABEL = {
  BOOK_NOW:           'Book Now',
  PAYMENT_REQUIRED:   'Payment Required',
  HOLD_FOR_LIQUIDITY: 'Hold · Liquidity',
  WATCH_CLOSELY:      'Watch Closely',
  MANUAL_REVIEW:      'Manual Review',
  UNKNOWN:            'Unknown',
};

const FINAL_ACTION_PILL = {
  BOOK_NOW:           'text-emerald-100 bg-emerald-950/55 border-emerald-400/35',
  PAYMENT_REQUIRED:   'text-rose-100 bg-rose-950/55 border-rose-400/35',
  HOLD_FOR_LIQUIDITY: 'text-brand-100 bg-brand-950/55 border-brand-400/35',
  WATCH_CLOSELY:      'text-brand-100 bg-brand-950/55 border-brand-400/30',
  MANUAL_REVIEW:      'text-brand-100 bg-brand-950/55 border-brand-400/35',
  UNKNOWN:            'text-stone-300 bg-zinc-900/55 border-white/10',
};

const GRADE_PILL = {
  A:       'text-emerald-200 bg-emerald-950/45 border-emerald-400/25',
  B:       'text-emerald-100 bg-emerald-950/35 border-emerald-400/20',
  C:       'text-brand-100 bg-brand-950/40 border-brand-400/20',
  D:       'text-orange-200 bg-orange-950/45 border-orange-400/25',
  F:       'text-rose-200 bg-rose-950/50 border-rose-400/30',
  Unknown: 'text-stone-400 bg-zinc-900/55 border-white/10',
};

const fmt$ = (cents) => {
  if (cents == null || !Number.isFinite(Number(cents))) return '—';
  const dollars = Number(cents) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(dollars);
};

const fmtPct = (v) => (v == null ? '—' : `${v}%`);
const fmtDays = (v) => (v == null ? '—' : `${v}d`);

const netColor = (cents) => {
  if (cents == null) return 'text-stone-400';
  const n = Number(cents);
  if (n > 0) return 'text-emerald-300';
  if (n < 0) return 'text-rose-300';
  return 'text-stone-400';
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ModelPerformance() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      const resp = await fetch('/api/admin/model-performance', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      const json = await resp.json();
      setData(json);
      setLastFetched(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('defera.user') || '{}');
      const roles = u?.app_metadata?.roles || [];
      return roles.includes('admin') || u?.role === 'admin';
    } catch { return false; }
  })();

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center text-stone-200 font-normal">
          <ShieldAlert className="w-8 h-8 mx-auto mb-3 text-rose-400/60" strokeWidth={1.25} />
          <p>Admin access required.</p>
          <Link to="/home" className="mt-4 inline-block text-sm text-stone-500 hover:text-white transition-colors">← Go home</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Model Performance · Defera Admin</title>
      </Helmet>

      <div className="min-h-screen bg-black text-stone-200 font-normal pt-20 pb-16 px-4 sm:px-6 max-w-7xl mx-auto">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Link to="/portfolio-intelligence" className="text-stone-600 hover:text-stone-400 transition-colors">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <h1 className="text-2xl font-extralight text-white tracking-wide">Model Performance</h1>
            </div>
            <p className="text-stone-400 text-sm font-normal">
              Ground-truth outcomes for every Portfolio & Trip AI decision. Analytics only — no automation.
            </p>
            {lastFetched && (
              <p className="text-stone-700 text-xs mt-1 tabular-nums">
                Last refreshed {lastFetched.toLocaleTimeString()}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            className="border border-white/10 text-stone-400 hover:text-white hover:bg-white/5 rounded-xl"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />
            Refresh
          </Button>
        </div>

        {/* ── Loading ── */}
        {loading && !data && (
          <div className="flex items-center justify-center py-24 gap-3 text-stone-500">
            <RefreshCw className="w-5 h-5 animate-spin" strokeWidth={1.25} />
            <span className="font-normal">Aggregating decisions…</span>
          </div>
        )}

        {/* ── Error ── */}
        {error && !loading && (
          <div className="rounded-xl border border-rose-400/25 bg-rose-950/25 text-rose-200 text-sm font-normal px-4 py-3 flex items-center gap-2">
            <XCircle className="w-4 h-4 shrink-0" strokeWidth={1.25} />
            {error}
          </div>
        )}

        {/* ── Empty ── */}
        {data?.empty && !loading && (
          <div className="rounded-xl border border-white/[0.06] bg-zinc-950/60 px-4 py-12 text-center">
            <ClipboardList className="w-6 h-6 text-brand-200/30 mx-auto mb-3" strokeWidth={1.25} />
            <p className="text-stone-400 font-normal">No outcome records yet.</p>
            <p className="text-stone-600 text-xs mt-1">
              Outcomes are seeded automatically as the AI engine processes active bookings.
            </p>
            <Link to="/risk-review" className="mt-4 inline-block text-xs text-brand-200/60 hover:text-brand-200 transition-colors">
              Open Risk Review →
            </Link>
          </div>
        )}

        {/* ── Dashboard ── */}
        {data && !data.empty && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
          >
            {/* 1. Overall AI Performance */}
            <OverallCard overall={data.overall} />

            {/* 2. Performance by Final Action */}
            <ByActionCard rows={data.by_final_action} />

            {/* 3. Performance by Risk Grade */}
            <ByGradeCard rows={data.by_risk_grade} />

            {/* 4 & 5. Routes + Airlines side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RouteAirlineCard title="Best / Worst Routes" keyField="route" rows={data.by_route} />
              <RouteAirlineCard title="Best / Worst Airlines" keyField="airline" rows={data.by_airline} />
            </div>

            {/* 6. Recent Completed Outcomes */}
            <RecentCompletedCard rows={data.recent_completed} />
          </motion.div>
        )}
      </div>
    </>
  );
}

// ─── Card shell ───────────────────────────────────────────────────────────────

function Card({ title, icon: Icon, children, className = '' }) {
  return (
    <section className={`rounded-2xl border border-white/[0.07] bg-gradient-to-br from-zinc-950/80 to-black/60 px-4 py-4 ${className}`}>
      <header className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-brand-200/60" strokeWidth={1.25} />}
        <h2 className="text-white/90 font-normal text-sm tracking-wide">{title}</h2>
      </header>
      {children}
    </section>
  );
}

// ─── 1. Overall KPIs ─────────────────────────────────────────────────────────

function OverallCard({ overall: o }) {
  if (!o) return null;
  const kpis = [
    { label: 'Total Decisions',     value: o.count,            accent: false },
    { label: 'Pending',             value: o.pending,          accent: false },
    { label: 'Won',                 value: o.won,              accent: 'emerald' },
    { label: 'Lost',                value: o.lost,             accent: 'rose' },
    { label: 'Neutral',             value: o.neutral,          accent: 'sky' },
    { label: 'Win Rate',            value: fmtPct(o.win_rate), accent: o.win_rate >= 50 ? 'emerald' : 'rose' },
  ];
  const pnl = [
    { label: 'Total Savings',  value: fmt$(o.realized_savings_cents), accent: 'emerald' },
    { label: 'Total Losses',   value: fmt$(o.realized_loss_cents),    accent: 'rose' },
    { label: 'Net P&L',        value: fmt$(o.net_pnl_cents),          accent: o.net_pnl_cents >= 0 ? 'emerald' : 'rose' },
    { label: 'Avg Days Waited', value: fmtDays(o.avg_days_waited),   accent: false },
    { label: 'Manual Overrides', value: `${o.manual_override_count} (${fmtPct(o.manual_override_rate)})`, accent: false },
    { label: 'Chargebacks',    value: `${o.chargeback_count} (${fmtPct(o.chargeback_rate)})`, accent: o.chargeback_count > 0 ? 'rose' : false },
    { label: 'Defaults',       value: `${o.default_count} (${fmtPct(o.default_rate)})`,       accent: o.default_count > 0 ? 'orange' : false },
  ];

  const accentClass = {
    emerald: 'text-emerald-300',
    rose:    'text-rose-300',
    sky:     'text-brand-300',
    orange:  'text-orange-300',
    false:   'text-stone-100',
  };

  const barData = ['won', 'lost', 'neutral', 'pending'].map((s) => ({
    name: OUTCOME_META[s].label,
    value: o[s] || 0,
    fill: OUTCOME_META[s].bar,
  }));

  return (
    <Card title="Overall AI Performance" icon={Activity}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {kpis.map((k) => (
          <KpiCell key={k.label} label={k.label} value={k.value} valueClass={accentClass[k.accent] || accentClass[false]} />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-5">
        {pnl.map((k) => (
          <KpiCell key={k.label} label={k.label} value={k.value} valueClass={accentClass[k.accent] || accentClass[false]} />
        ))}
      </div>
      {/* Outcome split bar chart */}
      {o.count > 0 && (
        <div className="h-36">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="name" tick={{ fill: '#78716c', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#78716c', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: '#0a0a0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, color: '#e7e5e4', fontSize: 12 }}
                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {barData.map((entry, i) => <Cell key={i} fill={entry.fill} fillOpacity={0.8} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function KpiCell({ label, value, valueClass = 'text-stone-100' }) {
  return (
    <div className="rounded-xl border border-white/[0.05] bg-zinc-950/40 px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-stone-600 font-medium mb-1">{label}</p>
      <p className={`text-lg font-light tabular-nums leading-none ${valueClass}`}>{value}</p>
    </div>
  );
}

// ─── 2. By Final Action ───────────────────────────────────────────────────────

function ByActionCard({ rows }) {
  if (!rows || rows.length === 0) return null;

  const barData = rows.map((r) => ({
    name: FINAL_ACTION_LABEL[r.final_action] || r.final_action,
    Won: r.won,
    Lost: r.lost,
    Neutral: r.neutral,
    Pending: r.pending,
  }));

  return (
    <Card title="Performance by Final Action" icon={ClipboardList}>
      <div className="h-44 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={barData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="name" tick={{ fill: '#78716c', fontSize: 9 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#78716c', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: '#0a0a0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, color: '#e7e5e4', fontSize: 11 }}
              cursor={{ fill: 'rgba(255,255,255,0.03)' }}
            />
            <Bar dataKey="Won"     stackId="a" fill="#34d399" fillOpacity={0.8} radius={[0,0,0,0]} />
            <Bar dataKey="Neutral" stackId="a" fill="#4E6199" fillOpacity={0.8} />
            <Bar dataKey="Lost"    stackId="a" fill="#f87171" fillOpacity={0.8} />
            <Bar dataKey="Pending" stackId="a" fill="#71717a" fillOpacity={0.6} radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <PerfTable
        rows={rows}
        keyField="final_action"
        keyLabel="Final Action"
        keyRenderer={(v) => (
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${FINAL_ACTION_PILL[v] || FINAL_ACTION_PILL.UNKNOWN}`}>
            {FINAL_ACTION_LABEL[v] || v}
          </span>
        )}
      />
    </Card>
  );
}

// ─── 3. By Risk Grade ────────────────────────────────────────────────────────

function ByGradeCard({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Card title="Performance by Customer Risk Grade" icon={ShieldAlert}>
      <PerfTable
        rows={rows}
        keyField="grade"
        keyLabel="Grade"
        keyRenderer={(v) => (
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${GRADE_PILL[v] || GRADE_PILL.Unknown}`}>
            {v}
          </span>
        )}
      />
    </Card>
  );
}

// ─── 4 & 5. Route / Airline ───────────────────────────────────────────────────

function RouteAirlineCard({ title, keyField, rows }) {
  const [sort, setSort] = useState('net_pnl_cents');
  const [dir, setDir]   = useState(-1); // -1 desc, 1 asc
  if (!rows || rows.length === 0) return null;

  const sorted = [...rows].sort((a, b) => {
    const av = a[sort] ?? -Infinity;
    const bv = b[sort] ?? -Infinity;
    return (bv - av) * dir;
  });

  const toggleSort = (col) => {
    if (sort === col) setDir((d) => d * -1);
    else { setSort(col); setDir(-1); }
  };

  const SortIcon = ({ col }) =>
    sort === col
      ? (dir === -1 ? <ChevronDown className="w-3 h-3 inline ml-0.5 text-brand-200/60" /> : <ChevronUp className="w-3 h-3 inline ml-0.5 text-brand-200/60" />)
      : null;

  return (
    <Card title={title} icon={TrendingUp}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-normal text-stone-300">
          <thead>
            <tr className="border-b border-white/[0.06] text-[10px] uppercase tracking-[0.15em] text-stone-600">
              <th className="text-left py-1.5 pr-3 font-medium">{keyField === 'route' ? 'Route' : 'Airline'}</th>
              <Th col="count"       label="#"      sort={sort} onClick={() => toggleSort('count')}><SortIcon col="count" /></Th>
              <Th col="win_rate"    label="Win %"  sort={sort} onClick={() => toggleSort('win_rate')}><SortIcon col="win_rate" /></Th>
              <Th col="net_pnl_cents" label="Net P&L" sort={sort} onClick={() => toggleSort('net_pnl_cents')}><SortIcon col="net_pnl_cents" /></Th>
              <Th col="avg_days_waited" label="Avg Wait" sort={sort} onClick={() => toggleSort('avg_days_waited')}><SortIcon col="avg_days_waited" /></Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r[keyField]} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                <td className="py-1.5 pr-3 text-stone-200 truncate max-w-[120px]">{r[keyField]}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right">{r.count}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right">{fmtPct(r.win_rate)}</td>
                <td className={`py-1.5 pr-2 tabular-nums text-right ${netColor(r.net_pnl_cents)}`}>{fmt$(r.net_pnl_cents)}</td>
                <td className="py-1.5 tabular-nums text-right">{fmtDays(r.avg_days_waited)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Th({ col, label, sort, onClick, children }) {
  return (
    <th
      className={`text-right py-1.5 pr-2 font-medium cursor-pointer select-none hover:text-stone-400 transition-colors ${sort === col ? 'text-brand-200/70' : ''}`}
      onClick={onClick}
    >
      {label}{children}
    </th>
  );
}

// ─── 6. Recent Completed ─────────────────────────────────────────────────────

function RecentCompletedCard({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Card title="Recent Completed Outcomes" icon={CheckCircle2}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-normal text-stone-300">
          <thead>
            <tr className="border-b border-white/[0.06] text-[10px] uppercase tracking-[0.15em] text-stone-600">
              <th className="text-left py-1.5 pr-3 font-medium">Route</th>
              <th className="text-left py-1.5 pr-3 font-medium">Airline</th>
              <th className="text-left py-1.5 pr-3 font-medium">Decision</th>
              <th className="text-left py-1.5 pr-3 font-medium">Grade</th>
              <th className="text-right py-1.5 pr-3 font-medium">Outcome</th>
              <th className="text-right py-1.5 pr-3 font-medium">Net P&L</th>
              <th className="text-right py-1.5 pr-3 font-medium">Wait</th>
              <th className="text-right py-1.5 font-medium">Booked</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const om = OUTCOME_META[r.outcome_status] || OUTCOME_META.pending;
              const OIcon = om.icon;
              return (
                <tr key={r.booking_id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                  <td className="py-1.5 pr-3 truncate max-w-[90px]">{r.route}</td>
                  <td className="py-1.5 pr-3 truncate max-w-[100px]">{r.airline}</td>
                  <td className="py-1.5 pr-3">
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium border ${FINAL_ACTION_PILL[r.final_action_at_decision] || FINAL_ACTION_PILL.UNKNOWN}`}>
                      {FINAL_ACTION_LABEL[r.final_action_at_decision] || r.final_action_at_decision || '—'}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3">
                    {r.customer_risk_grade && (
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium border ${GRADE_PILL[r.customer_risk_grade] || GRADE_PILL.Unknown}`}>
                        {r.customer_risk_grade}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium border ${om.pill}`}>
                      <OIcon className="w-2.5 h-2.5" strokeWidth={1.5} />
                      {om.label}
                    </span>
                  </td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${netColor(r.net_pnl_cents)}`}>{fmt$(r.net_pnl_cents)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{fmtDays(r.days_waited_after_decision)}</td>
                  <td className="py-1.5 text-right tabular-nums text-stone-500">
                    {r.booked_at ? new Date(r.booked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.some((r) => r.manual_override) && (
        <div className="mt-3 flex items-center gap-1.5 text-[10px] text-stone-400 font-normal">
          <AlertTriangle className="w-3 h-3 text-brand-400/50" strokeWidth={1.5} />
          Rows with a manual override may not reflect the AI recommendation.
        </div>
      )}
    </Card>
  );
}

// ─── Shared perf table ────────────────────────────────────────────────────────

function PerfTable({ rows, keyField, keyLabel, keyRenderer }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs font-normal text-stone-300">
        <thead>
          <tr className="border-b border-white/[0.06] text-[10px] uppercase tracking-[0.15em] text-stone-600">
            <th className="text-left py-1.5 pr-3 font-medium">{keyLabel}</th>
            <th className="text-right py-1.5 pr-2 font-medium">#</th>
            <th className="text-right py-1.5 pr-2 font-medium">Won</th>
            <th className="text-right py-1.5 pr-2 font-medium">Lost</th>
            <th className="text-right py-1.5 pr-2 font-medium">Neutral</th>
            <th className="text-right py-1.5 pr-2 font-medium">Pending</th>
            <th className="text-right py-1.5 pr-2 font-medium">Win %</th>
            <th className="text-right py-1.5 pr-2 font-medium">Savings</th>
            <th className="text-right py-1.5 pr-2 font-medium">Loss</th>
            <th className="text-right py-1.5 pr-2 font-medium">Net P&L</th>
            <th className="text-right py-1.5 font-medium">Avg Wait</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const net = r.net_pnl_cents;
            return (
              <tr key={r[keyField]} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                <td className="py-1.5 pr-3">{keyRenderer ? keyRenderer(r[keyField]) : r[keyField]}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right">{r.count}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right text-emerald-300/80">{r.won}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right text-rose-300/80">{r.lost}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right text-brand-300/80">{r.neutral}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right text-stone-500">{r.pending}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right">{fmtPct(r.win_rate)}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right text-emerald-300/80">{fmt$(r.realized_savings_cents)}</td>
                <td className="py-1.5 pr-2 tabular-nums text-right text-rose-300/80">{fmt$(r.realized_loss_cents)}</td>
                <td className={`py-1.5 pr-2 tabular-nums text-right ${netColor(net)}`}>{fmt$(net)}</td>
                <td className="py-1.5 tabular-nums text-right">{fmtDays(r.avg_days_waited)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
